import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import moment from 'moment';
import {socketClient}  from '../index';

const normalizeCalendarEvents = (payload) => {
  const appointments = Array.isArray(payload) ? payload : payload?.appointments || [];
  const events = [];

  for (let i = 0; i < appointments.length; i += 1) {
    const appointment = appointments[i];
    const allDay = moment(appointment.appointment_date).isBefore(appointment.appointment_date_end, 'day');
    events.push({
      id: appointment.id,
      color: appointment.technician?.color || '#0D4A80',
      allDay,
      concerns: appointment.concerns?.length ? [...appointment.concerns].sort((a, b) => a.id - b.id) : [],
      description: appointment.description,
      end: moment(appointment.appointment_date_end).isValid()
        ? moment(appointment.appointment_date_end).toDate().toISOString()
        : null,
      start: moment(appointment.appointment_date).isValid()
        ? moment(appointment.appointment_date).toDate().toISOString()
        : null,
      title: `${appointment.vehicle.year} ${appointment.vehicle.make} ${appointment.vehicle.model} - ${appointment.description}`,
      vehicle: appointment.vehicle,
      technician: appointment.technician,
      customer: appointment.vehicle.customer,
      send_reminder: appointment.send_reminder,
      dropoff: appointment.dropoff
    });
  }

  return events;
};

const normalizeCalendarTechnicians = (payload) =>
  Array.isArray(payload) ? payload : payload?.technicians || [];

const normalizeCalendarCustomers = (payload) => {
  const customers = [];

  if (payload && payload.length) {
    for (let i = 0; i < payload.length; i++) {
      const customer = payload[i];
      const fullName = [customer.name, customer.last_name].filter(Boolean).join(' ').trim();
      customers.push({
        id: customer.id,
        name: customer.name,
        last_name: customer.last_name,
        phone_number: customer.phone_number,
        label: `${fullName || customer.name} • ${customer.phone_number}`,
        vehicles: customer.vehicles || []
      });
    }
  }

  return customers;
};

// Every async request belongs to an authenticated account/shop generation. This
// is an in-memory presentation boundary; it never deletes persisted records.
export const isCalendarScopeCurrent = (expected, actual) => Boolean(
  expected?.identityId && expected?.shopId
  && expected.identityId === actual?.identityId
  && expected.shopId === actual?.shopId
  && expected.generation === actual?.generation
);

const staleScopeError = () => Object.assign(
  new Error('The calendar account or shop has changed. Please try again.'),
  { code: 'CALENDAR_SCOPE_CHANGED' }
);

export const isCalendarScopeStaleError = (error) => error?.code === 'CALENDAR_SCOPE_CHANGED';

const assertCurrentScope = (scope, getState) => {
  if (!isCalendarScopeCurrent(scope, getState().calendar.scope)
      || firebase.auth().currentUser?.uid !== scope.identityId) {
    throw staleScopeError();
  }
};

const calendarConnection = (scope, idToken) => {
  const connection = socketClient.socket;
  if (!connection?.connected || socketClient.authToken !== idToken
      || Number(socketClient.shopId) !== scope.shopId) {
    throw new Error('The calendar connection is not ready. Refresh after reconnecting.');
  }
  return connection;
};

const emitScopedCalendarRequest = ({
  connection, emitEvent, successEvent, errorEvent, payload, timeoutMessage, mapSuccess
}) => new Promise((resolve, reject) => {
  const cleanup = () => {
    clearTimeout(timeoutId);
    connection.off(successEvent, handleSuccess);
    connection.off(errorEvent, handleError);
    connection.off('disconnect', handleDisconnect);
  };
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) return;
    cleanup();
    try {
      resolve(mapSuccess ? mapSuccess(response) : response);
    } catch (error) {
      reject(error);
    }
  };
  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) return;
    cleanup();
    reject(new Error(response?.message || timeoutMessage));
  };
  const handleDisconnect = () => {
    cleanup();
    reject(new Error('The calendar connection changed. Refresh before trying again.'));
  };
  const timeoutId = setTimeout(() => {
    cleanup();
    reject(new Error(timeoutMessage));
  }, 10000);
  connection.on(successEvent, handleSuccess);
  connection.on(errorEvent, handleError);
  connection.on('disconnect', handleDisconnect);
  try {
    // Emit only on the checked, connected socket. SocketClient.emit can queue
    // across reconnects, which would carry a stale mutation into another scope.
    connection.emit(emitEvent, payload);
  } catch (error) {
    cleanup();
    reject(error);
  }
});

const emptyCalendar = () => ({
  events: [], eventsLoading: false, eventsError: null,
  technicians: [], techniciansLoading: false, techniciansError: null,
  customers: [], customersLoading: false, customersError: null,
  requests: { events: null, technicians: null, customers: null },
  eventWork: {
    readPending: false, readError: null, mutationError: null,
    pendingMutations: [], latestMutation: null, latestMutationIsolated: false,
    latestMutationByEvent: {}, reconcile: false
  },
  selectedCustomer: null, selectedVehicle: null, event: null,
  isDrawerOpen: false, selectedEventId: null, selectedRange: null
});

const initialState = {
  ...emptyCalendar(),
  scope: { identityId: null, shopId: null, generation: 0 }
};

const ownsRequest = (state, lane, action) =>
  isCalendarScopeCurrent(action.meta.arg.scope, state.scope)
  && state.requests[lane] === action.meta.requestId;

const hasPendingMutations = (state) => state.eventWork.pendingMutations.length > 0;
const updateEventStatus = (state) => {
  const work = state.eventWork;
  // Owed reconciliation is not itself an active request. A failed repair must
  // stop loading and await a later refresh/write, rather than retry in a loop.
  state.eventsLoading = work.readPending || hasPendingMutations(state);
  state.eventsError = work.mutationError || work.readError;
};

const replaceEvents = (state, events) => {
  state.events = events || [];
  // A deleted appointment cannot remain selected in a currently open drawer.
  if (state.selectedEventId !== null) {
    const selected = state.events.find((event) => String(event.id) === String(state.selectedEventId));
    state.event = selected || null;
    if (!selected) {
      state.selectedEventId = null;
      state.isDrawerOpen = false;
      state.selectedRange = null;
    }
  }
};

// A read overlapping a write may contain the pre-save snapshot, regardless of
// response order. Retire that read without giving it ownership of the write.
const retireEventRead = (state) => {
  state.requests.events = null;
  state.eventWork.readPending = false;
  state.eventWork.readError = null;
};

const requestStarted = (state, lane, action) => {
  if (!isCalendarScopeCurrent(action.meta.arg.scope, state.scope)) return;
  state.requests[lane] = action.meta.requestId;
  if (lane === 'events') {
    state.eventWork.readPending = true;
    state.eventWork.readError = null;
    updateEventStatus(state);
  } else {
    state[`${lane}Loading`] = true;
    state[`${lane}Error`] = null;
  }
};

const requestSucceeded = (state, lane, action) => {
  if (!ownsRequest(state, lane, action)) return;
  if (lane === 'events') {
    state.eventWork.readPending = false;
    state.eventWork.readError = null;
    if (!hasPendingMutations(state)) {
      replaceEvents(state, action.payload);
      // Only an accepted snapshot discharges the obligation, never read start.
      state.eventWork.reconcile = false;
    }
    updateEventStatus(state);
  } else {
    state[lane] = action.payload || [];
    state[`${lane}Loading`] = false;
    state[`${lane}Error`] = null;
  }
};

const requestFailed = (state, lane, action) => {
  if (!ownsRequest(state, lane, action)) return;
  const message = action.payload || action.error?.message || 'Unable to load calendar';
  if (lane === 'events') {
    state.eventWork.readPending = false;
    state.eventWork.readError = message;
    updateEventStatus(state);
  } else {
    state[`${lane}Loading`] = false;
    state[`${lane}Error`] = message;
  }
};

const mutationSettled = (state, action, failed) => {
  const { meta } = action;
  if (!isCalendarScopeCurrent(meta.arg.scope, state.scope)
      || !state.eventWork.pendingMutations.includes(meta.requestId)) return;
  retireEventRead(state);
  state.eventWork.pendingMutations = state.eventWork.pendingMutations.filter((id) => id !== meta.requestId);
  const eventKey = meta.eventId === undefined || meta.eventId === null
    ? null
    : String(meta.eventId);
  const ownsEventMutation = eventKey !== null
    && state.eventWork.latestMutationByEvent[eventKey] === meta.requestId;
  if (ownsEventMutation) {
    delete state.eventWork.latestMutationByEvent[eventKey];
    state.eventWork.mutationError = failed ? action.error.message : null;
    if (!failed) {
      const nextEvent = (action.payload || []).find((event) => String(event.id) === eventKey);
      if (nextEvent) {
        const currentIndex = state.events.findIndex((event) => String(event.id) === eventKey);
        if (currentIndex >= 0) state.events[currentIndex] = nextEvent;
        else state.events.push(nextEvent);
        if (state.event && String(state.event.id) === eventKey) state.event = nextEvent;
      }
      if (state.eventWork.latestMutationIsolated) state.eventWork.reconcile = false;
    }
  }
  if (state.eventWork.latestMutation === meta.requestId) {
    if (eventKey === null) state.eventWork.mutationError = failed ? action.error.message : null;
    if (!failed && eventKey === null) {
      replaceEvents(state, action.payload);
      // A write begun after all earlier writes settled can supply their current
      // snapshot itself. Overlapping writes cannot establish that ordering.
      if (state.eventWork.latestMutationIsolated) state.eventWork.reconcile = false;
    }
  }
  // With overlapping writes, neither invocation nor callback order establishes
  // database commit order. Keep the newer result, then request a fresh snapshot
  // after all local writes settle. A failed reconciliation remains visible.
  updateEventStatus(state);
};

const slice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    setCalendarScope(state, action) {
      const shopId = Number(action.payload?.shopId);
      const valid = action.payload?.identityId && Number.isSafeInteger(shopId) && shopId > 0;
      const next = {
        identityId: valid ? String(action.payload.identityId) : null,
        shopId: valid ? shopId : null
      };
      if (state.scope.identityId === next.identityId && state.scope.shopId === next.shopId) return state;
      return { ...emptyCalendar(), scope: { ...next, generation: state.scope.generation + 1 } };
    },
    mutationStarted(state, action) {
      const { meta } = action.payload;
      if (!isCalendarScopeCurrent(meta.arg.scope, state.scope)) return;
      retireEventRead(state);
      state.eventWork.latestMutationIsolated = !hasPendingMutations(state);
      if (!state.eventWork.latestMutationIsolated) state.eventWork.reconcile = true;
      state.eventWork.pendingMutations.push(meta.requestId);
      state.eventWork.latestMutation = meta.requestId;
      if (meta.eventId !== undefined && meta.eventId !== null) {
        state.eventWork.latestMutationByEvent[String(meta.eventId)] = meta.requestId;
      }
      state.eventWork.mutationError = null;
      updateEventStatus(state);
    },
    mutationSucceeded(state, action) { mutationSettled(state, action.payload, false); },
    mutationFailed(state, action) { mutationSettled(state, action.payload, true); },
    selectCustomer(state, action) {
      state.selectedCustomer = state.customers.find((customer) => String(customer.id) === String(action.payload.customerId)) || null;
      state.selectedVehicle = null;
    },
    selectVehicle(state, action) {
      state.selectedVehicle = state.selectedCustomer?.vehicles?.find((vehicle) => String(vehicle.id) === String(action.payload.vehicleId)) || null;
    },
    resetSelection(state) {
      state.selectedCustomer = null;
      state.selectedVehicle = null;
    },
    selectEvent(state, action) {
      const event = state.events.find((item) => String(item.id) === String(action.payload.eventId));
      if (!event) return;
      state.event = event;
      state.isDrawerOpen = true;
      state.selectedEventId = event.id;
      state.selectedRange = null;
    },
    selectRange(state, action) {
      if (!state.scope.identityId) return;
      state.isDrawerOpen = true;
      state.event = null;
      state.selectedEventId = null;
      state.selectedRange = action.payload;
    },
    openDrawerPanel(state) {
      if (!state.scope.identityId) return;
      state.isDrawerOpen = true;
    },
    closeDrawerPanel(state) {
      state.isDrawerOpen = false;
      state.event = null;
      state.selectedEventId = null;
      state.selectedRange = null;
      state.selectedCustomer = null;
      state.selectedVehicle = null;
    }
  },
  extraReducers: (builder) => {
    [
      ['calendar/requestGetAllEvents', 'events'],
      ['calendar/requestGetAllTechnicians', 'technicians'],
      ['requestGetRecentCustomers', 'customers'],
      ['calendar/searchForCustomer', 'customers']
    ].forEach(([type, lane]) => {
      builder.addCase(`${type}/pending`, (state, action) => requestStarted(state, lane, action));
      builder.addCase(`${type}/fulfilled`, (state, action) => requestSucceeded(state, lane, action));
      builder.addCase(`${type}/rejected`, (state, action) => requestFailed(state, lane, action));
    });
  }
});

export const reducer = slice.reducer;
export const { setCalendarScope } = slice.actions;

// Capture scope before pending is dispatched. Token acquisition itself may be
// delayed across logout/login, even when the next login has the same UID.
const scopedListThunk = (type, requestOptions) => {
  const thunk = createAsyncThunk(type, async ({ input, scope }, { getState, requestId, rejectWithValue }) => {
    try {
      assertCurrentScope(scope, getState);
      const idToken = await firebase.auth().currentUser.getIdToken();
      assertCurrentScope(scope, getState);
      const options = requestOptions(input);
      const result = await emitScopedCalendarRequest({
        ...options,
        connection: calendarConnection(scope, idToken),
        payload: { ...options.payload, idToken, requestId }
      });
      assertCurrentScope(scope, getState);
      return result;
    } catch (error) {
      // Reducers independently fence rejected results, including old timeouts.
      return rejectWithValue(error.message || 'Unable to load calendar');
    }
  }, {
    condition: ({ scope }, { getState }) => isCalendarScopeCurrent(scope, getState().calendar.scope)
  });
  return (input) => (dispatch, getState) => dispatch(thunk({ input, scope: { ...getState().calendar.scope } }));
};

const customerListOptions = () => ({
  emitEvent: 'getCustomers', successEvent: 'getCustomers', errorEvent: 'getCustomersError',
  timeoutMessage: 'Timed out while loading customers',
  mapSuccess: (response) => normalizeCalendarCustomers(response?.customers || [])
});

export const requestGetRecentCustomers = scopedListThunk('requestGetRecentCustomers', customerListOptions);
export const requestGetAllTechnicians = scopedListThunk('calendar/requestGetAllTechnicians', () => ({
  emitEvent: 'getTechnicians', successEvent: 'getTechnicians', errorEvent: 'getTechniciansError',
  timeoutMessage: 'Timed out while loading technicians', mapSuccess: normalizeCalendarTechnicians
}));
export const requestGetAllEvents = scopedListThunk('calendar/requestGetAllEvents', () => ({
  emitEvent: 'getAppointments', successEvent: 'getAppointments', errorEvent: 'getAppointmentsError',
  timeoutMessage: 'Timed out while loading appointments', mapSuccess: normalizeCalendarEvents
}));
export const searchForCustomer = scopedListThunk('calendar/searchForCustomer', (search) => (
  String(search || '').trim() ? {
    emitEvent: 'searchCustomer', successEvent: 'searchCustomer', errorEvent: 'searchCustomerError',
    payload: { search }, timeoutMessage: 'Timed out while searching customers',
    mapSuccess: (response) => normalizeCalendarCustomers(response?.customers || [])
  } : customerListOptions()
));

export const requestDeleteConcern = createAsyncThunk('requestDeleteConcern', async (id, { getState }) => {
  const scope = { ...getState().calendar.scope };
  assertCurrentScope(scope, getState);
  const idToken = await firebase.auth().currentUser.getIdToken();
  assertCurrentScope(scope, getState);
  calendarConnection(scope, idToken).emit('deleteConcern', { idToken, id });
});

const mutateEvents = (emitEvent, data, timeoutMessage, options = {}) => async (dispatch, getState) => {
  const scope = { ...getState().calendar.scope };
  assertCurrentScope(scope, getState);
  const requestId = `calendar:${emitEvent}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  const meta = { arg: { scope }, eventId: options.eventId, requestId };
  dispatch(slice.actions.mutationStarted({ meta }));
  try {
    const idToken = await firebase.auth().currentUser.getIdToken();
    assertCurrentScope(scope, getState);
    const events = await emitScopedCalendarRequest({
      connection: calendarConnection(scope, idToken),
      emitEvent, successEvent: 'getAppointments', errorEvent: 'getAppointmentsError',
      payload: { ...data, idToken, requestId }, timeoutMessage, mapSuccess: normalizeCalendarEvents
    });
    assertCurrentScope(scope, getState);
    dispatch(slice.actions.mutationSucceeded({ meta, payload: events }));
    return events;
  } catch (error) {
    // Never let old rejection handlers report errors into another account.
    assertCurrentScope(scope, getState);
    dispatch(slice.actions.mutationFailed({ meta, error: { message: error.message } }));
    throw error;
  } finally {
    const current = getState().calendar;
    if (isCalendarScopeCurrent(scope, current.scope)
        && current.eventWork.reconcile && !hasPendingMutations(current)
        && !current.eventWork.readPending) {
      // This read is independently scoped and reported; mutation success does
      // not imply its reconciliation succeeded. Never retry the business write.
      dispatch(requestGetAllEvents());
    }
  }
};

export const createEvent = (data) => mutateEvents('createAppointment', {
  description: data.descriptions[0], vehicle_id: data.vehicle, customer_id: data.customer,
  date: data.start, date_end: data.end, technician: data.technician, concerns: data.concerns,
  send_reminder: data.send_reminder, dropoff: data.dropoff,
  new_customer: data.newCustomer || null, new_vehicle: data.newVehicle || null
}, 'Timed out while creating appointment');
export const updateEvent = (eventId, update) => mutateEvents(
  'updateAppointment',
  { eventId, update },
  'Timed out while updating appointment',
  { eventId }
);
export const deleteEvent = (eventId) => mutateEvents('deleteAppointment', { id: eventId }, 'Timed out while deleting appointment');

export const selectEvent = (eventId) => (dispatch) => dispatch(slice.actions.selectEvent({ eventId }));
export const selectCustomer = (customerId) => (dispatch) => dispatch(slice.actions.selectCustomer({ customerId }));
export const selectVehicle = (vehicleId) => (dispatch) => dispatch(slice.actions.selectVehicle({ vehicleId }));
export const selectRange = (start, end) => (dispatch) => dispatch(slice.actions.selectRange({ start: start.getTime(), end: end.getTime() }));
export const openDrawerPanel = () => (dispatch) => dispatch(slice.actions.openDrawerPanel());
export const closeDrawerPanel = () => (dispatch) => dispatch(slice.actions.closeDrawerPanel());
export const resetSelection = () => (dispatch) => dispatch(slice.actions.resetSelection());

export default slice;
